/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/*
Author: Gilang Romadhanu Tartila
*/

import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Formik } from 'formik';
import { addDays } from 'date-fns';
import * as Yup from 'yup';
import Toast from 'react-native-toast-message';
import { View, ScrollView } from 'react-native';
import { buttonFloating } from '@/assets/styles/buttonStyles';
import { isWeb } from '@global/screens';
import { isDisableSubmit } from '@global/helpers';
import Colors from '@global/colors';
import SociollaText from '@components/sociolla/SociollaText';
import SelectBox from '@components/form/SelectBox';
import Button from '@components/form/Button';
import Input from '@components/form/Input';
import DatePicker from '@components/form/DatePicker';
import * as Layout from '@components/screen/Layout';

import useApi from '@/hooks/useApi';
import { MsOrder } from '@api/ms-order';
import { showPopup } from '@/store/modal';

const BudgetPocketFormScreen = (props) => {
  const dispatch = useDispatch();

  const [isEdit, setIsEdit] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const [detailData, setDetailData] = useState({});

  const FormValidationSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required('Name is required.')
      .test(
        'alphabets',
        'Budget name must contain alphabets',
        (value) => /[^0-9]/.test(value) && /[a-zA-Z]/.test(value),
      ),
    description: Yup.string().required('Bucket Value is required.'),
    value: Yup.string().required('Bucket Value is required.'),
    start_date: Yup.string().required('Date From is required.'),
    end_date: Yup.string().required('Date To is required.'),
  });

  const getBudgetPocketDetail = useApi(MsOrder.getBudgetPocketDetail);

  const getDetail = async (id) => {
    const response = await getBudgetPocketDetail.request(id);
    if (response?.data?.data && Object.keys(response.data.data).length) {
      const result = response.data.data;
      setDetailData(result);
    }
  };

  const onChangeActive = async (isActive) => {
    try {
      if (isEdit) {
        await MsOrder.updateStatusActiveBudgetPocket(detailData?._id, {
          is_active: isActive,
        });
      }
    } catch (error) {}
  };

  useEffect(() => {
    let mounted = true;
    if (mounted) {
      setDetailData({});
      setIsEdit(false);
      setIsReady(false);
      if (props?.route?.params?.id) {
        setIsEdit(true);
        getDetail(props.route.params.id);
      } else {
        setIsEdit(false);
      }
      setTimeout(() => {
        setIsReady(true);
      }, 1000);
    }
    return () => {
      mounted = false;
    };
  }, [props]);

  return isReady &&
    (isEdit
      ? !(getBudgetPocketDetail && getBudgetPocketDetail.loading) &&
        detailData &&
        Object.keys(detailData).length
      : true) ? (
    <>
      <Layout.Container>
        <Formik
          initialValues={{
            name: isEdit ? detailData.name : '',
            value: isEdit ? detailData.value : '',
            description: isEdit ? detailData.description : '',
            start_date: isEdit ? detailData.start_date : '',
            end_date: isEdit ? detailData.end_date : '',
            current_status: isEdit ? detailData.current_status : 'draft',
            is_active:
              isEdit && detailData.is_active
                ? {
                    name: detailData.is_active ? 'Active' : 'Inactive',
                    value: detailData.is_active ? true : false,
                  }
                : { name: 'Inactive', value: false },
          }}
          validationSchema={FormValidationSchema}
          onSubmit={async (payload) => {
            dispatch(showPopup({ name: 'isLoading', value: true }));
            try {
              const finalPayload = {
                name: payload.name,
                value: payload.value,
                description: payload.description,
                start_date: payload.start_date,
                end_date: payload.end_date,
                is_active: payload.is_active?.value ? true : false,
                // current_status: payload.current_status,
              };

              let statusChanged = false;
              if (!isEdit) {
                finalPayload.current_status = payload.current_status;
              } else if (
                isEdit &&
                detailData.current_status &&
                detailData.current_status != payload.current_status
              ) {
                statusChanged = true;
              }

              if (isEdit) {
                let updateData = null;
                if (statusChanged) {
                  updateData = await MsOrder.changeStatusBudgetPocket(
                    props.route.params.id,
                    payload.current_status,
                    finalPayload,
                  );
                } else {
                  updateData = await MsOrder.updateBudgetPocket(
                    props.route.params.id,
                    finalPayload,
                  );
                }
                if (updateData?.data?.data) {
                  const updatedData = updateData.data.data;
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully edited data',
                    type: 'success',
                  });
                  setTimeout(() => {
                    props.navigation.reset({
                      index: 0,
                      routes: [
                        {
                          name: 'BudgetPocketDetailScreen',
                          params: {
                            titleSection: updatedData.name,
                            name: updatedData.name,
                            id: updatedData._id,
                            reload: true,
                          },
                        },
                      ],
                    });
                  }, 500);
                } else {
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                }
              } else {
                const createData = await MsOrder.createBudgetPocket(
                  finalPayload,
                );
                if (createData?.data?.data) {
                  const detail = createData.data.data;
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'Successfully added data',
                    type: 'success',
                  });
                  if (detail && detail._id) {
                    setTimeout(() => {
                      props.navigation.reset({
                        index: 0,
                        routes: [
                          {
                            name: 'BudgetPocketDetailScreen',
                            params: {
                              titleSection: detailData.name || detail.name,
                              name: detailData.name || detail.name,
                              id: detailData._id || detail._id,
                              reload: true,
                            },
                          },
                        ],
                      });
                    }, 500);
                  } else {
                    props.navigation.navigate('BudgetPocketScreen');
                  }
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: 'A system error occurred',
                    type: 'error',
                  });
                }
              }
            } catch (error) {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({
                text1:
                  error?.response?.data?.message || 'A system error occurred',
                type: 'error',
              });
            }
          }}>
          {({
            handleChange,
            setFieldValue,
            validateForm,
            handleSubmit,
            errors,
            touched,
            values,
          }) => (
            <>
              {isWeb ? (
                <Layout.Wrapper white={false} hasProgressBar>
                  <Layout.Back
                    title="Budget Pocket"
                    onCallback={() => {
                      if (isEdit) {
                        props.navigation.navigate('BudgetPocketDetailScreen', {
                          titleScreen: props.route.params.name,
                          name: props.route.params.name,
                          id: props.route.params.id,
                        });
                      } else {
                        props.navigation.navigate('BudgetPocketScreen');
                      }
                    }}
                  />
                  <Layout.Title
                    title={
                      isEdit ? 'Edit Budget Pocket' : 'Create Budget Pocket'
                    }>
                    <View style={{ flexDirection: 'row' }}>
                      <Button
                        rounded
                        buttonStyle={{ width: 120 }}
                        disabledStyle={{ width: 120 }}
                        disabled={isDisableSubmit(values)}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            {isEdit ? 'Update' : 'Save'}
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          evt.persist();
                          setFieldValue('current_status', 'draft');
                          setTimeout(() => {
                            validateForm().then(() => {
                              handleSubmit(evt);
                            });
                          }, 100);
                        }}
                      />
                      <Button
                        rounded
                        buttonStyle={{ width: 160, marginLeft: 15 }}
                        disabledStyle={{ width: 160, marginLeft: 15 }}
                        disabled={isDisableSubmit(values)}
                        title={
                          <SociollaText
                            type="bold"
                            style={{
                              fontSize: 14,
                              letterSpacing: 0.5,
                              color: Colors.white,
                            }}>
                            Request Approval
                          </SociollaText>
                        }
                        onPress={(evt) => {
                          evt.persist();
                          setFieldValue('current_status', 'need_approval');
                          setTimeout(() => {
                            validateForm().then(() => {
                              handleSubmit(evt);
                            });
                          }, 100);
                        }}
                      />
                    </View>
                  </Layout.Title>
                </Layout.Wrapper>
              ) : (
                false
              )}
              <Layout.Wrapper rounded bottomSpace={!isWeb ? 70 : 0} isHeight>
                <ScrollView>
                  <Layout.FormWrapper
                    style={{
                      width: isWeb ? '50%' : '100%',
                      flexDirection: isWeb ? 'row' : 'column',
                      paddingHorizontal: 15,
                      ...(isWeb
                        ? {
                            paddingTop: 20,
                            paddingVertical: 20,
                          }
                        : {
                            paddingTop: 10,
                          }),
                    }}>
                    <Layout.FormGroup>
                      <Input
                        label="Budget Name"
                        value={values.name}
                        isRequired
                        hasError={
                          errors.name && touched.name ? errors.name : null
                        }
                        onChangeText={handleChange('name')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <Input
                        label="Budget Description"
                        value={values.description}
                        isRequired
                        hasError={
                          errors.description && touched.description
                            ? errors.description
                            : null
                        }
                        onChangeText={handleChange('description')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <Input
                        label="Budget Value"
                        value={values.value}
                        isRequired
                        isNumeric
                        hasError={
                          errors.value && touched.value ? errors.value : null
                        }
                        onChangeText={handleChange('value')}
                      />
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <View style={{ width: '100%', flexDirection: 'row' }}>
                        <DatePicker
                          label="From Date"
                          name="start_date"
                          style={{
                            marginRight: isWeb ? 10 : 0,
                            width: isWeb ? 'calc(50% - 5px)' : '50%',
                          }}
                          value={values.start_date}
                          disablePastDates
                          hasError={
                            errors.start_date && touched.start_date
                              ? errors.start_date
                              : null
                          }
                          onChange={(value) => {
                            setFieldValue(
                              'end_date',
                              addDays(new Date(value), 1),
                            );
                            setFieldValue('start_date', value);
                          }}
                        />
                        <DatePicker
                          label="To Date"
                          name="end_date"
                          style={{ width: isWeb ? 'calc(50% - 5px)' : '50%' }}
                          value={values.end_date}
                          disablePastDates
                          disableBeforeDate={values.start_date}
                          hasError={
                            errors.end_date && touched.end_date
                              ? errors.end_date
                              : null
                          }
                          onChange={(value) => setFieldValue('end_date', value)}
                        />
                      </View>
                    </Layout.FormGroup>
                    <Layout.FormGroup>
                      <SelectBox
                        label="Status"
                        options={[
                          { name: 'Active', value: true },
                          { name: 'Inactive', value: false },
                        ]}
                        value={values.is_active}
                        trackBy="value"
                        labelKey="name"
                        isRequired
                        hasError={
                          errors.is_active && touched.is_active
                            ? errors.is_active
                            : null
                        }
                        onChange={(value) => {
                          setFieldValue('is_active', value);
                          onChangeActive(value.value);
                        }}
                      />
                    </Layout.FormGroup>
                  </Layout.FormWrapper>
                </ScrollView>
              </Layout.Wrapper>
              {!isWeb ? (
                <View style={buttonFloating}>
                  <Button
                    rounded
                    buttonStyle={{ width: '100%', marginBottom: 10 }}
                    disabledStyle={{ width: '100%', marginBottom: 10 }}
                    disabled={isDisableSubmit(values)}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        Request Approval
                      </SociollaText>
                    }
                    onPress={(evt) => {
                      evt.persist();
                      setFieldValue('current_status', 'need_approval');
                      setTimeout(() => {
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      }, 100);
                    }}
                  />
                  <Button
                    rounded
                    buttonStyle={{ width: '100%' }}
                    disabledStyle={{ width: '100%' }}
                    disabled={isDisableSubmit(values)}
                    title={
                      <SociollaText
                        type="bold"
                        style={{
                          fontSize: 14,
                          letterSpacing: 0.5,
                          color: Colors.white,
                        }}>
                        {isEdit ? 'Update' : 'Save'}
                      </SociollaText>
                    }
                    onPress={(evt) => {
                      evt.persist();
                      setFieldValue('current_status', 'draft');
                      setTimeout(() => {
                        validateForm().then(() => {
                          handleSubmit(evt);
                        });
                      }, 100);
                    }}
                  />
                </View>
              ) : (
                false
              )}
            </>
          )}
        </Formik>
      </Layout.Container>
    </>
  ) : (
    <Layout.Container>
      <Layout.Wrapper rounded>
        <Layout.Loader />
      </Layout.Wrapper>
    </Layout.Container>
  );
};

export default BudgetPocketFormScreen;
